import { yieldTask } from '../../../util/schedule.js';
import { accrualOrder, accrueInterest, accruesIn, activateShadows, } from './accrual.js';
import { noteClaimSteps } from './claims.js';
import { startOf } from './dates.js';
import { capitalize } from './derived.js';
import { applyWaivers, nextInternalStop, processStop } from './limitation.js';
import { syncAmounts, writeWriteOffAmounts } from './output.js';
// How long the replay runs before yielding to the event loop, so a long
// calculation doesn't block the server or the browser.
const YIELD_AFTER_MS = 4;
export const yieldIfDue = async (caseState) => {
    if (Date.now() - caseState.yieldedAt > YIELD_AFTER_MS) {
        await yieldTask();
        caseState.yieldedAt = Date.now();
    }
};
// Accrues up to `to`, stopping at limitation events on the way.
export const advance = async (caseState, to) => {
    if (caseState.accruedTo === null) {
        caseState.accruedTo = to;
        processStop(caseState, to);
        return;
    }
    while (caseState.accruedTo < to) {
        const from = caseState.accruedTo;
        activateShadows(caseState, from);
        const stop = Math.min(to, nextInternalStop(caseState, from));
        for (const entry of accrualOrder(caseState)) {
            await yieldIfDue(caseState);
            accrueInterest(caseState, entry, from, stop);
        }
        caseState.accruedTo = stop;
        processStop(caseState, stop);
    }
};
// One of v1's breakpoint steps ending at `to`.
const closeBreakpointStep = (caseState, to, type) => {
    const from = caseState.stepFrom;
    for (const entry of caseState.interestEntries) {
        const interest = entry.interest;
        if (type !== null &&
            entry.appended &&
            interest.compoundingType === type &&
            accruesIn(interest, caseState.until, from, to)) {
            capitalize(entry, to);
        }
    }
    applyWaivers(caseState);
    const callback = caseState.options.breakPointCallBack;
    if (callback !== undefined) {
        syncAmounts(caseState);
        for (const entry of caseState.ledgerEntries) {
            writeWriteOffAmounts(entry);
        }
        if (caseState.options.writeOffAccruals === true) {
            noteClaimSteps(caseState, from, to);
        }
        callback(caseState.entries
            .filter((entry) => entry.appended)
            .map((entry) => entry.voucher), startOf(from), startOf(to));
    }
    caseState.stepFrom = to;
};
// Runs v1's breakpoint steps up to the instant `at` of the next append.
export const catchUp = async (caseState, at) => {
    if (caseState.pastUntil) {
        return;
    }
    const end = Math.min(at, caseState.until);
    if (caseState.lastAppendAt === null) {
        await advance(caseState, end);
        caseState.stepFrom = end;
        return;
    }
    if (caseState.lastAppendAt > end) {
        return;
    }
    const { breakpoints } = caseState;
    while (caseState.nextBreakpoint < breakpoints.length &&
        breakpoints[caseState.nextBreakpoint].date < caseState.lastAppendAt) {
        caseState.nextBreakpoint++;
    }
    while (caseState.nextBreakpoint < breakpoints.length &&
        breakpoints[caseState.nextBreakpoint].date < end) {
        const breakpoint = breakpoints[caseState.nextBreakpoint++];
        await advance(caseState, breakpoint.date);
        closeBreakpointStep(caseState, breakpoint.date, breakpoint.type);
    }
    await advance(caseState, end);
    closeBreakpointStep(caseState, end, null);
    if (at > caseState.until) {
        caseState.pastUntil = true;
    }
};
export const markAppended = (caseState, entry, at) => {
    if (!entry.appended) {
        entry.appended = true;
        caseState.appendedByTier[entry.tier].push(entry);
    }
    caseState.lastAppended = entry;
    noteAppendAt(caseState, at);
};
// Breakpoints before the last instant appended at within `until` are past.
export const noteAppendAt = (caseState, at) => {
    if (!caseState.pastUntil && at <= caseState.until) {
        caseState.lastAppendAt = Math.max(caseState.lastAppendAt ?? at, at);
    }
};
export const takeSnapshots = (caseState, upTo) => {
    const times = caseState.options.snapshotTimes ?? [];
    if (caseState.nextSnapshot >= times.length ||
        times[caseState.nextSnapshot] > upTo) {
        return;
    }
    syncAmounts(caseState);
    const amounts = new Map(caseState.entries.map((entry) => [
        entry.caseVoucherId,
        entry.voucher.amount,
    ]));
    while (caseState.nextSnapshot < times.length &&
        times[caseState.nextSnapshot] <= upTo) {
        caseState.snapshots.set(times[caseState.nextSnapshot++], amounts);
    }
};
//# sourceMappingURL=timeline.js.map